import {
  FORMS_CALLBACK_URL,
  FORMS_DISCOUNT_URL,
  FORMS_RESERVE_URL
} from '@theme/forms-api-config';

const OOS_RESERVE_ENABLED = true;

class PopupFormsManager {
  constructor() {
    this.popups = new Map();
    this.formCooldownMs = 120000;
    this.init();
  }

  init() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
  }

  setup() {
    const handleClick = (e) => {
      const trigger = e.target.closest('[data-reserve-product], [data-popup-trigger]');
      if (trigger) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        
        this.handleTriggerClick(trigger);
        
        return false;
      }
    };
    
    document.addEventListener('mousedown', handleClick, true);
    document.addEventListener('click', handleClick, true);
  }

  handleTriggerClick(trigger) {
    if (OOS_RESERVE_ENABLED && trigger.hasAttribute('data-reserve-product')) {
      this.openReservePopup(trigger);
    } else if (trigger.hasAttribute('data-popup-trigger')) {
      const popupId = trigger.getAttribute('data-popup-trigger');
      if (popupId === 'delivery-location') {
        if (typeof window.closeMobileMenuDrawer === 'function') {
          window.closeMobileMenuDrawer();
        }
        if (typeof window.openDeliveryLocationPopup === 'function') {
          window.openDeliveryLocationPopup();
        }
        return;
      }
      const targetId = popupId === 'gift' ? 'gift-order' : popupId;
      const hasConfig = this.popups.has(targetId);
      const hasPopupElement = document.getElementById(`am-popup-${targetId}`);
      if (!hasConfig && !hasPopupElement) {
        return;
      }
      if (hasConfig) {
        this.renderPopup(targetId);
      }
      if (typeof window.openPopup === 'function') {
        window.openPopup(targetId);
      }
      if (targetId === 'gift-order' || targetId === 'special-request') {
        this.prefillCartNoteForm(targetId);
      }
    }
  }

  async prefillCartNoteForm(popupId) {
    const popupEl = document.getElementById(`am-popup-${popupId}`);
    const form = popupEl?.querySelector('form');
    if (!form) return;
    try {
      const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      const cart = await res.json();
      const attrs = cart.attributes || {};
      if (popupId === 'gift-order') {
        const giftMsg = form.querySelector('[name="gift_message"]');
        const recv = form.querySelector('[name="receiver_name"]');
        const send = form.querySelector('[name="sender_name"]');
        if (giftMsg) giftMsg.value = attrs.gift_message || '';
        if (recv) recv.value = attrs.receiver_name || '';
        if (send) send.value = attrs.sender_name || '';
        const withTab = form.querySelector('[data-gift-tab="with"]');
        const withoutTab = form.querySelector('[data-gift-tab="without"]');
        form.classList.remove('am-popup__form--without-message');
        if (withTab) {
          withTab.classList.add('is-active');
          withTab.setAttribute('aria-selected', 'true');
        }
        if (withoutTab) {
          withoutTab.classList.remove('is-active');
          withoutTab.setAttribute('aria-selected', 'false');
        }
      } else {
        const special = form.querySelector('[name="special_request"]');
        if (special) special.value = (attrs.special_request || '').toString();
      }
    } catch (error) {
      console.warn('Failed to prefill popup form from cart attributes', error);
    }
  }

  openReservePopup(trigger) {
    const variantId = trigger.dataset.variantId || '';
    const productPath = trigger.dataset.productPath || '';
    const productUrl = productPath
      ? this.buildReserveProductUrl(productPath, variantId)
      : this.normalizeReserveProductUrl(trigger.dataset.productUrl, variantId);

    const data = {
      productId: trigger.dataset.productId,
      variantId: variantId,
      title: trigger.dataset.productTitle,
      image: trigger.dataset.productImage,
      productUrl: productUrl,
      productPath: productPath,
      sku: trigger.dataset.variantSku
    };

    if (window.openReservePopup) {
      window.openReservePopup(data);
    }
  }

  registerPopup(popupId, config) {
    this.popups.set(popupId, {
      ...config,
      isRendered: false
    });
  }

  renderPopup(popupId) {
    const popup = this.popups.get(popupId);
    if (!popup || popup.isRendered) return;

    const template = document.getElementById(`${popupId}-template`);
    const container = document.getElementById(`${popupId}-container`);

    if (template && container) {
      container.replaceChildren();
      if (template instanceof HTMLTemplateElement) {
        container.appendChild(template.content.cloneNode(true));
      } else {
        template.childNodes.forEach((node) => {
          container.appendChild(node.cloneNode(true));
        });
      }
      popup.isRendered = true;

      this.initPopupForm(popupId);

      if (popup.onRender) {
        popup.onRender();
      }
    }
  }

  initPopupForm(popupId) {
    const popup = this.popups.get(popupId);
    if (!popup) return;

    const popupElement = document.getElementById(`am-popup-${popupId}`);
    if (!popupElement) return;

    const form = popupElement.querySelector('form');
    if (!form) return;
    this.applyFieldLimits(form);

    if (popup.formEndpoint) {
      form.addEventListener('submit', (e) => {
        e.preventDefault();
        this.submitForm(form, popup.formEndpoint, popup.formOptions || {});
      });
      if ((popupId === 'callback' || popupId === 'reserve-item') && popup.onRender) {
        popup.onRender();
      }
      return;
    }

    if (popupId === 'gift-order' || popupId === 'special-request') {
      form.addEventListener('submit', (e) => {
        e.preventDefault();
        this.saveCartNoteForm(popupId, form);
      });
    }
    if (popupId === 'gift-order') {
      this.initGiftTabs(form);
    }
  }

  initGiftTabs(form) {
    const tabs = form.querySelectorAll('[data-gift-tab]');
    tabs.forEach((tab) => {
      tab.addEventListener('click', () => {
        const isWith = tab.getAttribute('data-gift-tab') === 'with';
        form.classList.toggle('am-popup__form--without-message', !isWith);
        tabs.forEach((t) => {
          t.classList.toggle('is-active', t === tab);
          t.setAttribute('aria-selected', t === tab ? 'true' : 'false');
        });
      });
    });
  }

  buildOrderNote(giftMessage, receiverName, senderName, specialRequest) {
    const giftLines = [];
    if (giftMessage && giftMessage.trim()) giftLines.push(`Gift message: ${giftMessage.trim()}`);
    if (receiverName && receiverName.trim()) giftLines.push(`Receiver's name: ${receiverName.trim()}`);
    if (senderName && senderName.trim()) giftLines.push(`Sender's name: ${senderName.trim()}`);
    const giftBlock = giftLines.length ? giftLines.join('\n') : '-';
    const specialBlock = (specialRequest && specialRequest.trim()) ? specialRequest.trim() : '-';
    return `Gift Request:\n${giftBlock}\n\nSpecial Request:\n${specialBlock}`;
  }

  async saveCartNoteForm(popupId, form) {
    const submitBtn = form.querySelector('[type="submit"]');
    const originalText = submitBtn?.textContent || '';
    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.textContent = 'Saving...';
    }
    try {
      const cartRes = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      const cart = await cartRes.json();
      const attrs = cart.attributes || {};
      let giftMessage = '';
      let receiverName = '';
      let senderName = '';
      let specialRequest = (attrs.special_request || '').toString();
      if (popupId === 'gift-order') {
        const withoutMessage = form.classList.contains('am-popup__form--without-message');
        if (!withoutMessage) {
          giftMessage = (form.querySelector('[name="gift_message"]')?.value || '').toString();
          receiverName = (form.querySelector('[name="receiver_name"]')?.value || '').toString();
          senderName = (form.querySelector('[name="sender_name"]')?.value || '').toString();
        }
      } else {
        specialRequest = (form.querySelector('[name="special_request"]')?.value || '').toString();
        giftMessage = (attrs.gift_message || '').toString();
        receiverName = (attrs.receiver_name || '').toString();
        senderName = (attrs.sender_name || '').toString();
      }
      const note = this.buildOrderNote(giftMessage, receiverName, senderName, specialRequest);
      const payload = { note };
      if (popupId === 'gift-order') {
        payload.attributes = {
          gift_message: giftMessage,
          receiver_name: receiverName,
          sender_name: senderName,
          special_request: attrs.special_request || ''
        };
      } else {
        payload.attributes = {
          gift_message: attrs.gift_message || '',
          receiver_name: attrs.receiver_name || '',
          sender_name: attrs.sender_name || '',
          special_request: specialRequest
        };
      }
      const updateRes = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await updateRes.json();
      if (!updateRes.ok) throw new Error(data.description || 'Cart update failed');
      const noteInput = document.querySelector('form#cart-form input[name="note"]');
      if (noteInput) noteInput.value = note;
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: data } }));
      if (typeof window.closePopup === 'function') window.closePopup(popupId);
    } catch (error) {
      console.warn('Failed to save cart note form', error);
    } finally {
      if (submitBtn) {
        submitBtn.disabled = false;
        submitBtn.textContent = originalText;
      }
    }
  }

  setPopupData(popupId, data) {
    this.renderPopup(popupId);

    const popup = this.popups.get(popupId);
    if (popup && popup.setData) {
      popup.setData(data);
    }
  }

  stripTags(value) {
    return String(value || '').replace(/<[^>]*>/g, '').trim();
  }

  sanitizePlainText(value, maxLength = 500) {
    const normalized = this.stripTags(value).replace(/[\u0000-\u001F\u007F]/g, ' ');
    return normalized.slice(0, maxLength);
  }

  sanitizeNumericId(value) {
    const normalized = String(value || '').replace(/[^\d]/g, '').slice(0, 32);
    return normalized;
  }

  sanitizeSku(value) {
    return this.sanitizePlainText(value, 120).replace(/[^\w\-./ ]/g, '');
  }

  isSafeUrlProtocol(url) {
    const protocol = String(url?.protocol || '').toLowerCase();
    return protocol === 'https:' || protocol === 'http:';
  }

  sanitizeImageUrl(imageUrl) {
    const raw = String(imageUrl || '').trim();
    if (!raw) return '';

    try {
      const url = new URL(raw, window.location.origin);
      if (!this.isSafeUrlProtocol(url)) return '';
      return url.toString();
    } catch (_) {
      return '';
    }
  }

  sanitizeProductPath(productPath) {
    const raw = String(productPath || '').trim();
    if (!raw) return '';
    const withoutOrigin = raw.replace(/^https?:\/\/[^/]+/i, '');
    const pathOnly = withoutOrigin.split('#')[0].split('?')[0].trim();
    if (!pathOnly.startsWith('/')) return '';
    if (/[\u0000-\u001F\u007F<>"'`\\]/.test(pathOnly)) return '';
    return pathOnly.slice(0, 500);
  }

  sanitizeReserveProductData(data) {
    const safeData = data && typeof data === 'object' ? data : {};
    const variantId = this.sanitizeNumericId(safeData.variantId);
    const productPath = this.sanitizeProductPath(safeData.productPath);

    return {
      productId: this.sanitizeNumericId(safeData.productId),
      variantId: variantId,
      title: this.sanitizePlainText(safeData.title, 160),
      image: this.sanitizeImageUrl(safeData.image),
      productPath: productPath,
      productUrl: this.normalizeReserveProductUrl(safeData.productUrl, variantId),
      sku: this.sanitizeSku(safeData.sku)
    };
  }

  setFormMessage(messageEl, text, type) {
    if (!messageEl) return;
    messageEl.textContent = text;
    messageEl.className = 'am-popup__message';
    if (type === 'error') {
      messageEl.classList.add('am-popup__message--error');
    } else if (type === 'success') {
      messageEl.classList.add('am-popup__message--success');
    }
    messageEl.style.display = 'block';
  }

  applyFieldLimits(formElement) {
    formElement.querySelectorAll('input[type="email"]').forEach((input) => {
      input.maxLength = 320;
    });
    formElement.querySelectorAll('input[type="text"], input[type="tel"], input:not([type])').forEach((input) => {
      if (input.type === 'email') return;
      input.maxLength = 120;
    });
  }

  buildReserveProductUrl(productPath, variantId) {
    const path = this.sanitizeProductPath(productPath);
    const id = this.sanitizeNumericId(variantId);
    if (!path || !id) return '';
    return `${window.location.origin}${path}?variant=${encodeURIComponent(id)}`;
  }

  normalizeReserveProductUrl(productUrl, variantId) {
    const id = this.sanitizeNumericId(variantId);
    if (!id) return '';
    const stored = String(productUrl || '').trim();
    if (!stored) return '';
    try {
      const url = new URL(stored, window.location.origin);
      if (!this.isSafeUrlProtocol(url)) return '';
      if (url.origin !== window.location.origin) {
        return this.buildReserveProductUrl(url.pathname, id);
      }
      return this.buildReserveProductUrl(url.pathname, id);
    } catch (_) {
      const pathOnly = stored.split('?')[0];
      return this.buildReserveProductUrl(pathOnly, id);
    }
  }

  getFieldLimit(fieldName) {
    const key = String(fieldName || '').toLowerCase();
    if (key.includes('url')) return 500;
    if (key.includes('email')) return 320;
    if (key.includes('name')) return 120;
    if (key.includes('phone')) return 120;
    if (key.includes('business')) return 120;
    if (key.includes('position')) return 120;
    if (key.includes('country')) return 120;
    if (key.includes('type')) return 120;
    if (key.includes('capacity')) return 120;
    if (key.includes('size')) return 120;
    if (key.includes('rooms')) return 120;
    return null;
  }

  sanitizePayload(data) {
    const sanitized = {};
    Object.entries(data).forEach(([key, value]) => {
      if (typeof value !== 'string') {
        sanitized[key] = value;
        return;
      }
      const cleanValue = this.stripTags(value);
      const limit = this.getFieldLimit(key);
      sanitized[key] = limit ? cleanValue.slice(0, limit) : cleanValue;
    });
    return sanitized;
  }

  parseRetryAfterMs(response, fallbackMs) {
    const retryAfter = Number(response.headers.get('Retry-After'));
    if (Number.isFinite(retryAfter) && retryAfter > 0) {
      return retryAfter * 1000;
    }
    return fallbackMs;
  }

  normalizePopupPhone(rawPhone, dialCode) {
    const raw = String(rawPhone || '');
    const dial = String(dialCode || '').trim();
    let cleanPhone = raw.replace(/^\+\d{1,4}\s*/, '');
    cleanPhone = cleanPhone.replace(/^\s*[A-Z]{2,3}\s+/, '');
    cleanPhone = cleanPhone.replace(/\s+[A-Z]{2,3}\s+/g, ' ');
    cleanPhone = cleanPhone.replace(/\s+[A-Z]{2,3}\s*$/, '');
    cleanPhone = cleanPhone.replace(/[^\d]/g, '');
    return {
      local: cleanPhone,
      full: dial && cleanPhone ? `${dial}${cleanPhone}` : cleanPhone
    };
  }

  validateReserveForm(formElement, data, messageEl) {
    if (!formElement.reportValidity()) {
      return false;
    }

    data.product_id = this.sanitizeNumericId(data.product_id);
    data.variant_id = this.sanitizeNumericId(data.variant_id);
    data.product_title = this.sanitizePlainText(data.product_title, 160);
    data.sku = this.sanitizeSku(data.sku);
    data.product_url = this.normalizeReserveProductUrl(data.product_url, data.variant_id);

    const requiredMetaFields = ['product_id', 'variant_id', 'product_title', 'product_url'];
    const hasMissingMeta = requiredMetaFields.some((field) => !String(data[field] || '').trim());
    if (hasMissingMeta) {
      this.setFormMessage(messageEl, 'Reserve data is incomplete. Please reselect the variant and try again.', 'error');
      return false;
    }

    const countryCodeDisplay = formElement.querySelector('[data-country-dial-display]');
    const dialCode = countryCodeDisplay?.textContent?.trim() || '';
    const normalizedPhone = this.normalizePopupPhone(data.phone, dialCode);
    if (normalizedPhone.local.length < 6 || normalizedPhone.local.length > 15) {
      const phoneInput = formElement.querySelector('[data-phone-input]');
      if (phoneInput) {
        phoneInput.setCustomValidity(phoneInput.getAttribute('title') || 'Please enter a valid phone number');
        phoneInput.reportValidity();
        phoneInput.addEventListener('input', () => phoneInput.setCustomValidity(''), { once: true });
      }
      return false;
    }

    data.phone = normalizedPhone.full;
    data.dialCode = dialCode;
    return true;
  }

  setSubmitCooldown(submitBtn, originalText, cooldownMs) {
    const until = Date.now() + cooldownMs;
    submitBtn.dataset.cooldownUntil = String(until);
    submitBtn.disabled = true;
    setTimeout(() => {
      const currentUntil = Number(submitBtn.dataset.cooldownUntil || '0');
      if (Date.now() < currentUntil) return;
      submitBtn.disabled = false;
      submitBtn.textContent = originalText;
      delete submitBtn.dataset.cooldownUntil;
    }, cooldownMs);
  }

  async submitForm(formElement, endpoint, options = {}) {
    const submitBtn = formElement.querySelector('[type="submit"]');
    const messageEl = formElement.querySelector('.am-popup__message');
    
    if (!submitBtn) return;

    const originalText = submitBtn.textContent;
    const cooldownUntil = Number(submitBtn.dataset.cooldownUntil || '0');
    if (Date.now() < cooldownUntil) {
      if (messageEl) {
        messageEl.textContent = 'Too many submissions. Please try again in a minute';
        messageEl.classList.add('am-popup__message--error');
        messageEl.style.display = 'block';
      }
      return { success: false, error: new Error('Rate limited') };
    }

    const isOptimistic = options.optimistic === true;
    
    if (isOptimistic) {
      submitBtn.disabled = true;
      if (messageEl) {
        messageEl.textContent = options.successMessage || 'Thank you! Your request has been submitted.';
        messageEl.classList.add('am-popup__message--success');
        messageEl.style.display = 'block';
      }
      if (options.hideFormOnSuccess) {
        formElement.classList.add('am-popup__form--success');
      }
    } else {
      submitBtn.disabled = true;
      submitBtn.textContent = options.loadingText || 'Sending...';
      if (messageEl) {
        messageEl.style.display = 'none';
        messageEl.className = 'am-popup__message';
      }
    }
 
    try {
      const formData = new FormData(formElement);
      const data = this.sanitizePayload(Object.fromEntries(formData));

      // Убираем countryCode из данных, если он есть
      delete data.countryCode;

      const formType = formElement.getAttribute('data-form-type');
      if (formType === 'reserve-item') {
        data.product_url = this.normalizeReserveProductUrl(data.product_url, data.variant_id);
        if (!this.validateReserveForm(formElement, data, messageEl)) {
          submitBtn.disabled = false;
          submitBtn.textContent = originalText;
          return { success: false, error: new Error('Validation failed') };
        }
      }

      if (formType === 'callback' || formType === 'reserve-item') {
        const countryCodeDisplay = formElement.querySelector('[data-country-dial-display]');
        const dialCode = countryCodeDisplay?.textContent?.trim() || '';
        const phone = data.phone || '';
        if (dialCode && phone) {
          const normalizedPhone = this.normalizePopupPhone(phone, dialCode);
          data.phone = normalizedPhone.full;
          data.dialCode = dialCode;
        }
      }

      if (options.extraData) {
        Object.assign(data, options.extraData);
      }

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        credentials: 'same-origin',
        body: JSON.stringify(data)
      });

      if (response.status === 429) {
        const cooldownMs = this.parseRetryAfterMs(response, this.formCooldownMs);
        this.setFormMessage(messageEl, 'Too many submissions. Please try again in a minute', 'error');
        this.setSubmitCooldown(submitBtn, originalText, cooldownMs);
        return { success: false, error: new Error('Rate limited') };
      }

      if (!response.ok) {
        let errorText = '';
        try {
          const errorJson = await response.json();
          errorText = errorJson?.error || errorJson?.message || '';
        } catch (_) {
          errorText = await response.text();
        }
        throw new Error(errorText || 'Request failed');
      }

      if (!isOptimistic) {
        if (messageEl) {
          messageEl.textContent = options.successMessage || 'Thank you! Your request has been submitted.';
          messageEl.classList.add('am-popup__message--success');
          messageEl.style.display = 'block';
        }

        if (options.hideFormOnSuccess) {
          formElement.classList.add('am-popup__form--success');
        }
      }

      if (options.onSuccess) {
        options.onSuccess(data);
      }

      if (options.closeOnSuccess !== false) {
        setTimeout(() => {
          const popupId = formElement.closest('.am-popup')?.dataset.popupId;
          if (popupId) {
            window.closePopup(popupId);
            formElement.reset();
            formElement.classList.remove('am-popup__form--success');
          }
          if (messageEl) {
            messageEl.style.display = 'none';
          }
        }, options.closeDelay || 2000);
      }

      return { success: true, data };
    } catch (error) {
      if (isOptimistic && messageEl) {
        formElement.classList.remove('am-popup__form--success');
        messageEl.classList.remove('am-popup__message--success');
        messageEl.textContent = options.errorMessage || 'Sorry, something went wrong. Please try again.';
        messageEl.classList.add('am-popup__message--error');
        messageEl.style.display = 'block';
        submitBtn.disabled = false;
        submitBtn.textContent = originalText;
      } else if (messageEl) {
        messageEl.textContent = options.errorMessage || 'Sorry, something went wrong. Please try again.';
        messageEl.classList.add('am-popup__message--error');
        messageEl.style.display = 'block';
      }

      if (options.onError) {
        options.onError(error);
      }

      return { success: false, error };
    } finally {
      const until = Number(submitBtn.dataset.cooldownUntil || '0');
      if (Date.now() >= until && !isOptimistic) {
        submitBtn.disabled = false;
        submitBtn.textContent = originalText;
      }
    }
  }

  updatePopupImage(popupId, imageUrl, altText = '') {
    const popup = document.getElementById(`am-popup-${popupId}`);
    if (!popup) return;

    const imageContainer = popup.querySelector('.am-popup__image');
    if (!imageContainer) return;

    const header = imageContainer.querySelector('.am-popup__header');
    const headerClone = header ? header.cloneNode(true) : null;

    imageContainer.replaceChildren();

    const safeImageUrl = this.sanitizeImageUrl(imageUrl);
    const safeAltText = this.sanitizePlainText(altText, 160);

    if (safeImageUrl) {
      const img = document.createElement('img');
      img.src = safeImageUrl;
      img.alt = safeAltText;
      img.className = 'am-popup__image-img';
      img.loading = 'lazy';
      img.referrerPolicy = 'no-referrer';
      imageContainer.appendChild(img);
      imageContainer.style.display = 'flex';
    } else {
      imageContainer.style.display = 'none';
    }

    if (headerClone) {
      imageContainer.appendChild(headerClone);
    }
  }

  setElementText(elementId, text) {
    const element = document.getElementById(elementId);
    if (element) {
      element.textContent = this.sanitizePlainText(text, 500);
    }
  }

  setElementsTextByClass(className, text) {
    const elements = document.querySelectorAll(`.${className}`);
    elements.forEach(element => {
      element.textContent = this.sanitizePlainText(text, 500);
    });
  }

  setInputValue(elementId, value) {
    const element = document.getElementById(elementId);
    if (element) {
      element.value = this.sanitizePlainText(value, 500);
    }
  }
}

window.popupFormsManager = new PopupFormsManager();

const POPUP_CONFIGS = {
  'reserve-item': {
    formEndpoint: FORMS_RESERVE_URL,
    formOptions: {
      loadingText: 'Sending...',
      successMessage: 'Thank you! We will contact you when the item is available.',
      errorMessage: 'Sorry, something went wrong. Please try again.',
      closeOnSuccess: true,
      closeDelay: 2000
    },
    setData: function(data) {
      const safeData = window.popupFormsManager.sanitizeReserveProductData(data);
      const productUrl = safeData.productPath
        ? window.popupFormsManager.buildReserveProductUrl(safeData.productPath, safeData.variantId)
        : window.popupFormsManager.normalizeReserveProductUrl(safeData.productUrl, safeData.variantId);

      window.popupFormsManager.setElementsTextByClass('reserve-product-title', safeData.title);
      window.popupFormsManager.setInputValue('reserve-product-title-hidden', safeData.title);
      window.popupFormsManager.setInputValue('reserve-product-id', safeData.productId);
      window.popupFormsManager.setInputValue('reserve-variant-id', safeData.variantId);
      window.popupFormsManager.setInputValue('reserve-product-url', productUrl);
      window.popupFormsManager.setInputValue('reserve-variant-sku', safeData.sku);
      window.popupFormsManager.updatePopupImage('reserve-item', safeData.image, safeData.title);
    },
    onRender: function() {
      setTimeout(() => {
        const form = document.getElementById('reserve-item-form');
        if (form && typeof window.initCountryPhoneSelector === 'function') {
          delete form.dataset.countryPhoneReady;
          window.initCountryPhoneSelector(form);
        }
      }, 100);
    }
  },
  'newsletter-discount': {
    formEndpoint: FORMS_DISCOUNT_URL,
    formOptions: {
      optimistic: true,
      loadingText: 'Sending...',
      successMessage: 'Your discount code has been sent to your email!',
      errorMessage: 'Sorry, something went wrong. Please try again.',
      closeOnSuccess: false,
      closeDelay: 0,
      hideFormOnSuccess: true
    }
  },
  'callback': {
    formEndpoint: FORMS_CALLBACK_URL,
    formOptions: {
      loadingText: 'Sending...',
      successMessage: 'Thank you! We will call you back soon.',
      errorMessage: 'Sorry, something went wrong. Please try again.',
      closeOnSuccess: true,
      closeDelay: 2000
    },
    onRender: function() {
      setTimeout(() => {
        const form = document.getElementById('callback-form');
        if (form && typeof window.initCountryPhoneSelector === 'function') {
          delete form.dataset.countryPhoneReady;
          window.initCountryPhoneSelector(form);
        }
      }, 100);
    }
  },
  'gift-order': {},
  'special-request': {}
};

function initPopupSystem() {
  Object.entries(POPUP_CONFIGS).forEach(([popupId, config]) => {
    window.popupFormsManager.registerPopup(popupId, config);
  });
  
  window.openReservePopup = function(productData) {
    if (window.popupFormsManager) {
      window.popupFormsManager.renderPopup('reserve-item');
      window.popupFormsManager.setPopupData('reserve-item', productData);
    }
    if (window.openPopup) window.openPopup('reserve-item');
  };
  
  window.openNewsletterPopup = function() {
    if (window.popupFormsManager) window.popupFormsManager.renderPopup('newsletter-discount');
    if (window.openPopup) window.openPopup('newsletter-discount');
  };
  
  window.openCallBackPopup = function(event) {
    if (event) {
      event.preventDefault();
    }
    if (window.popupFormsManager) window.popupFormsManager.renderPopup('callback');
    if (window.openPopup) window.openPopup('callback');
  };
  
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initPopupSystem);
} else {
  initPopupSystem();
}
